// BaselineDataView.jsx
//
// Two-section editor:
//   1) Contacts & Balance  (model-level, per DPD bucket - 8 rows × 2 inputs)
//   2) Per-channel grids   (per channel, per DPD bucket)
//
// As of the funnel rework, each channel is ONE table that interleaves the
// four manual rate inputs with the nine derived columns:
//
//   DPD | Outreach | Outreach$ | RPC% | RPC | 24hr Pay% | 24hr Pay $
//       | PTP of RPC% | PTP% | PTP Kept% | PTP Success% | Collections%
//       | Total Collections | Total Collections$
//
//   Manual inputs (stored): RPC%, 24hr Pay%, PTP of RPC%, PTP Kept%
//     - all entered as FRACTIONS in 0..1 (e.g. 0.4 = 40%), matching the
//       existing rate-entry convention.
//   Derived (live, not stored):
//     Outreach          = contacts(DPD) × channel share for the day
//     Outreach$         = balance(DPD)  × channel share for the day
//     RPC               = reach × RPC%
//     24hr Pay $        = reach$ × 24hr Pay%
//     PTP%              = RPC% × PTP of RPC%
//     PTP Success%      = PTP% × PTP Kept%
//     Collections%      = 24hr Pay% + PTP Success%
//     Total Collections = Collections% × reach
//     Total Collections$= Collections% × reach$
//
//   "reach" defaults to the share-based Outreach, BUT when a channel/day has
//   a Capacity entered it switches to a capacity-based reach:
//       capPct = Capacity ÷ total unique accounts (for the day)
//   capPct then drives RPC, 24hr Pay $, Total Collections and Total
//   Collections$ in place of the share. Outreach / Outreach$ themselves stay
//   share-based (the assigned figures). The channel header RPC% is computed
//   straight from the columns: Σ RPC ÷ Σ Outreach.
//
// Capacity is a per-channel/day manual field stored on
// analysis.model_channel_day_shares.capacity, saved via the
// channel-day-attempts endpoint as part of "Save All".
//
// Section 1 (contacts/balance) drives Section 2's Outreach figures live -
// no save needed between edits. One "Save All" persists both via a single
// PUT to /forecast/models/:id/baseline.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Checkbox, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem,
  Select, Snackbar, TextField, Typography,
} from "@mui/material";

import {
  getForecastModels,
  getModelBaseline,
  saveModelBaseline,
  saveChannelDayAttempts,
} from "../../services/forecast/forecastservices";
import {
  BLUE, GREEN, AMBER, PURPLE, PINK,
  ActionButton, EmptyState, SectionTitle,
} from "./forecastShared";


// Colors assigned by display_order, not by name - works with whatever
// channel naming convention the user has set up.
const CHANNEL_COLORS = [BLUE, GREEN, PURPLE, AMBER, PINK];

// ISO 8601 day numbering: 1=Mon..7=Sun. Matches the calendar.day_of_week
// column populated by sp_populate_calendar.
const DAY_NAMES = [
  { num: 1, short: "Mon", long: "Monday" },
  { num: 2, short: "Tue", long: "Tuesday" },
  { num: 3, short: "Wed", long: "Wednesday" },
  { num: 4, short: "Thu", long: "Thursday" },
  { num: 5, short: "Fri", long: "Friday" },
  { num: 6, short: "Sat", long: "Saturday" },
  { num: 7, short: "Sun", long: "Sunday" },
];


// ── Helpers ────────────────────────────────────────────────────────
function safeNum(v) { const n = Number(v); return isNaN(n) ? 0 : n; }
function clamp01(n) { return Math.max(0, Math.min(1, safeNum(n))); }

function fmtCurrency(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "$0";
  if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1_000)     return `$${(num / 1_000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

// Comma-separated integer (for count columns: Outreach, RPC, Total Collections)
function fmtCount(n) {
  const num = Number(n);
  if (!isFinite(num)) return "0";
  return Math.round(num).toLocaleString();
}

function fmtPct(num, decimals = 2) {
  const n = Number(num);
  if (!isFinite(n)) return "—";
  return `${(n * 100).toFixed(decimals)}%`;
}


// ── Numeric input cell (right-aligned, compact) ────────────────────
function NumInput({ value, onChange, disabled, isRate = false }) {
  // Manual-entry numeric field. Implementation notes:
  //
  // - type="text" + inputMode="decimal": no spinner arrows, no wheel-
  //   scroll value changes, no locale formatting surprises. Mobile
  //   still gets a numeric keyboard via inputMode.
  //
  // - Draft state: while the field is focused, we display exactly what
  //   the user is typing (so "0.4" doesn't collapse to "0" mid-typing).
  //   On blur, we fall back to showing the parsed value, or empty if 0.
  //
  // - Empty when 0: an unfocused field holding 0 renders blank rather
  //   than "0". The underlying state still holds 0 and saves as 0.
  const [draft, setDraft] = React.useState("");
  const [focused, setFocused] = React.useState(false);

  const displayValue = focused
    ? draft
    : ((value === 0 || value == null) ? "" : value);

  return (
    <TextField
      size="small" type="text" fullWidth
      inputProps={{
        inputMode: "decimal",
        style: { fontSize: 12, textAlign: "right", padding: "5px 8px" },
      }}
      value={displayValue}
      onFocus={() => {
        setDraft((value === 0 || value == null) ? "" : String(value));
        setFocused(true);
      }}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        const raw = e.target.value;
        // Accept empty, digits, and at most one decimal point. Anything
        // else (letters, e/E, +/-, multiple dots) is silently rejected
        // - the draft stays at its last valid string.
        if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
          setDraft(raw);
          if (raw === "" || raw === ".") {
            onChange(0);
          } else {
            const n = Number(raw);
            if (!isNaN(n)) {
              onChange(isRate ? Math.min(1, Math.max(0, n)) : Math.max(0, n));
            }
          }
        }
      }}
      disabled={disabled}
    />
  );
}


// ── Integer input (empty = null, not 0) ────────────────────────────
// Used for Capacity. Unlike NumInput (which collapses an empty field to 0),
// this keeps the empty state as null so we can persist NULL — the signal
// that there's nothing entered. Integers only (it's a count).
function CapacityInput({ value, onChange, disabled }) {
  const [draft, setDraft] = React.useState("");
  const [focused, setFocused] = React.useState(false);

  const displayValue = focused ? draft : (value == null ? "" : value);

  return (
    <TextField
      size="small" type="text"
      inputProps={{
        inputMode: "numeric",
        style: { fontSize: 12, textAlign: "right", padding: "3px 6px", width: 70 },
      }}
      value={displayValue}
      onFocus={() => { setDraft(value == null ? "" : String(value)); setFocused(true); }}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        const raw = e.target.value;
        // digits only; empty clears to null, else integer >= 0
        if (raw === "" || /^\d*$/.test(raw)) {
          setDraft(raw);
          if (raw === "") onChange(null);
          else {
            const n = parseInt(raw, 10);
            if (!isNaN(n)) onChange(Math.max(0, n));
          }
        }
      }}
      disabled={disabled}
    />
  );
}


// ── Read-only calculated cell ──────────────────────────────────────
function CalcCell({ value, label, color = "text.secondary", bold = false }) {
  return (
    <Box sx={{
      textAlign: "right",
      fontSize: 12,
      fontWeight: bold ? 600 : 400,
      color,
      px: "8px", py: "5px",
      background: "rgba(0,0,0,0.025)",
      borderRadius: 0.5,
    }}>
      {value}
      {label && (
        <Box sx={{ fontSize: 10, color: "text.disabled", fontWeight: 400, mt: "1px" }}>
          {label}
        </Box>
      )}
    </Box>
  );
}


export default function BaselineDataView() {
  const [models,         setModels]         = useState([]);
  const [modelId,        setModelId]        = useState("");
  const [contactsRows,   setContactsRows]   = useState([]);  // ALL 7 days × 8 buckets per model
  const [metricsRows,    setMetricsRows]    = useState([]);  // ALL 7 days × 8 × n_channels per model
  // channelDayShares: [{ channel_id, channel_name, day_of_week, share_of_contacts }]
  // Loaded from GET /baseline. Configured on Models tab (ModelFormModal).
  // Read-only here - the Baseline tab uses it to derive per-channel Outreach.
  const [channelDayShares, setChannelDayShares] = useState([]);
  // capacityMap: manually-entered "Capacity" per channel/day, keyed
  // `${channel_id}|${day_of_week}` → integer | null. When set, the channel's
  // outcome columns (RPC / 24hr Pay $ / Total Coll / Total Coll $) use
  // capacity ÷ total unique accounts in place of the channel share.
  // Lives on analysis.model_channel_day_shares.capacity; saved in "Save All".
  const [capacityMap, setCapacityMap] = useState({});
  const [selectedDay,    setSelectedDay]    = useState(1);   // 1=Mon..7=Sun (ISO)
  const [duplicateOpen,  setDuplicateOpen]  = useState(false);
  const [duplicateTargets, setDuplicateTargets] = useState([]);  // array of day numbers
  // Section collapse state - holds the IDs of sections that are
  // currently collapsed. Defaults to fully collapsed.
  const [collapsedSet, setCollapsedSet] = useState(new Set(["contacts"]));
  const initializedForModelRef = useRef(null);

  const [loadingModels, setLoadingModels] = useState(true);
  const [loadingRows,   setLoadingRows]   = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState("");
  const [toast,         setToast]         = useState({ open: false, message: "", severity: "success" });

  // ── Load models ──────────────────────────────────────────────
  useEffect(() => {
    setLoadingModels(true);
    getForecastModels()
      .then((data) => {
        const list = (data || []).filter((m) => m.is_active);
        setModels(list);
        if (list.length > 0) setModelId(list[0].id);
      })
      .catch(() => setError("Failed to load models"))
      .finally(() => setLoadingModels(false));
  }, []);

  // ── Load baseline ────────────────────────────────────────────
  const loadBaseline = useCallback(async () => {
    if (!modelId) {
      setContactsRows([]); setMetricsRows([]); setChannelDayShares([]); setCapacityMap({});
      return;
    }
    setLoadingRows(true); setError("");
    try {
      const data = await getModelBaseline(modelId);

      const contacts = (data?.model_contacts || []).map((r) => ({
        dpd_bucket_id:    r.dpd_bucket_id,
        bucket_label:     r.bucket_label,
        day_of_week:      r.day_of_week,
        contacts_count:   safeNum(r.contacts_count),
        contacts_balance: safeNum(r.contacts_balance),
      }));

      const metrics = (data?.channel_metrics || []).map((r) => ({
        channel_id:                    r.channel_id,
        channel_name:                  r.channel_name,
        channel_share_of_contacts:     safeNum(r.channel_share_of_contacts),  // legacy fallback
        dpd_bucket_id:                 r.dpd_bucket_id,
        bucket_label:                  r.bucket_label,
        day_of_week:                   r.day_of_week,
        // four manual rate inputs (fractions 0..1)
        rpc_rate:                      safeNum(r.rpc_rate),
        conversion_24hr_payment_rate:  safeNum(r.conversion_24hr_payment_rate),
        ptp_of_rpc_rate:               safeNum(r.ptp_of_rpc_rate),
        conversion_kept_rate:          safeNum(r.conversion_kept_rate),
      }));

      const shares = (data?.channel_day_shares || []).map((r) => ({
        channel_id:        r.channel_id,
        channel_name:      r.channel_name,
        day_of_week:       r.day_of_week,
        share_of_contacts: safeNum(r.share_of_contacts),
      }));

      // Capacity values (nullable). Only keep non-null entries.
      const capacities = {};
      (data?.channel_day_shares || []).forEach((r) => {
        if (r.capacity != null) {
          capacities[`${r.channel_id}|${r.day_of_week}`] = Number(r.capacity);
        }
      });

      setContactsRows(contacts);
      setMetricsRows(metrics);
      setChannelDayShares(shares);
      setCapacityMap(capacities);
    } catch (e) {
      setError(`Failed to load baseline: ${e?.response?.data?.error || e?.message}`);
    } finally {
      setLoadingRows(false);
    }
  }, [modelId]);

  useEffect(() => { loadBaseline(); }, [loadBaseline]);

  // ── Setters ──────────────────────────────────────────────────
  const setContactsCell = (dpd_bucket_id, key, value) => {
    const n = Number(value);
    setContactsRows((prev) => prev.map((r) =>
      r.dpd_bucket_id === dpd_bucket_id && r.day_of_week === selectedDay
        ? { ...r, [key]: isNaN(n) ? 0 : n } : r
    ));
  };

  const setMetricsCell = (channel_id, dpd_bucket_id, key, value) => {
    const n = Number(value);
    setMetricsRows((prev) => prev.map((r) =>
      r.channel_id === channel_id && r.dpd_bucket_id === dpd_bucket_id && r.day_of_week === selectedDay
        ? { ...r, [key]: isNaN(n) ? 0 : n }
        : r
    ));
  };

  // ── Duplicate the currently-selected day's data into target days ─
  const handleDuplicateConfirm = () => {
    if (duplicateTargets.length === 0) {
      setDuplicateOpen(false);
      return;
    }

    setContactsRows((prev) => {
      const sourceRows = prev.filter((r) => r.day_of_week === selectedDay);
      const others     = prev.filter((r) => !duplicateTargets.includes(r.day_of_week));
      const copies     = duplicateTargets.flatMap((day) =>
        sourceRows.map((r) => ({ ...r, day_of_week: day }))
      );
      return [...others, ...copies];
    });

    setMetricsRows((prev) => {
      const sourceRows = prev.filter((r) => r.day_of_week === selectedDay);
      const others     = prev.filter((r) => !duplicateTargets.includes(r.day_of_week));
      const copies     = duplicateTargets.flatMap((day) =>
        sourceRows.map((r) => ({ ...r, day_of_week: day }))
      );
      return [...others, ...copies];
    });

    const srcName = DAY_NAMES.find(d => d.num === selectedDay)?.short || "";
    const tgtNames = duplicateTargets
      .map((n) => DAY_NAMES.find(d => d.num === n)?.short)
      .filter(Boolean).join(", ");
    setToast({
      open: true,
      severity: "success",
      message: `Copied ${srcName} to ${tgtNames}. Click Save All to persist.`,
    });
    setDuplicateOpen(false);
    setDuplicateTargets([]);
  };

  const toggleDuplicateTarget = (day) => {
    setDuplicateTargets((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const selectAllDuplicateTargets = () => {
    const all = DAY_NAMES.map((d) => d.num).filter((n) => n !== selectedDay);
    setDuplicateTargets(duplicateTargets.length === all.length ? [] : all);
  };

  // Day-filtered views: render and calculations operate on the selected day only.
  const contactsForDay = useMemo(
    () => contactsRows.filter((r) => r.day_of_week === selectedDay),
    [contactsRows, selectedDay]
  );

  const metricsForDay = useMemo(
    () => metricsRows.filter((r) => r.day_of_week === selectedDay),
    [metricsRows, selectedDay]
  );

  // ── Lookup: contacts/balance for a given DPD bucket on the selected day ─
  const contactsByDpd = useMemo(() => {
    const map = new Map();
    contactsForDay.forEach((r) => map.set(r.dpd_bucket_id, r));
    return map;
  }, [contactsForDay]);

  // ── Group channel metrics by channel (selected day only) ────
  const channelGroups = useMemo(() => {
    const map = new Map();
    metricsForDay.forEach((r) => {
      if (!map.has(r.channel_id)) {
        map.set(r.channel_id, {
          channel_id:                r.channel_id,
          channel_name:              r.channel_name,
          channel_share_of_contacts: r.channel_share_of_contacts,
          rows: [],
        });
      }
      map.get(r.channel_id).rows.push(r);
    });
    return Array.from(map.values());
  }, [metricsForDay]);

  // Per-day share lookup for the currently-selected day. Returns the
  // share_of_contacts (0..1) for the given channel on selectedDay,
  // falling back to the legacy channel_personas.share if no day-share
  // row exists.
  const getShareForDay = (channel_id, channel_share_fallback = 0) => {
    const s = channelDayShares.find(
      x => x.channel_id === channel_id && x.day_of_week === selectedDay
    );
    return s ? s.share_of_contacts : channel_share_fallback;
  };

  // Capacity get/set for the currently-selected day. null = not entered.
  const getCapacity = (channel_id) => {
    const v = capacityMap[`${channel_id}|${selectedDay}`];
    return v == null ? null : v;
  };
  const setCapacity = (channel_id, value) => {
    setCapacityMap((prev) => ({
      ...prev,
      [`${channel_id}|${selectedDay}`]: value == null ? null : Number(value),
    }));
  };

  // ── Per-row funnel (computed in render) ─────────────────────
  // shareForDay drives the share-based Outreach / Outreach$ (the assigned
  // figures). outcomeShare drives the outcome columns (RPC, 24hr Pay $,
  // Total Coll, Total Coll $); it equals shareForDay unless a Capacity is
  // set, in which case it's capacity ÷ total unique accounts.
  const derive = (mRow, shareForDay, outcomeShare) => {
    const oShare   = outcomeShare == null ? shareForDay : outcomeShare;
    const c        = contactsByDpd.get(mRow.dpd_bucket_id);
    const contacts = c?.contacts_count   || 0;
    const balance  = c?.contacts_balance || 0;

    const outreach   = contacts * shareForDay;   // count  (assigned, share-based)
    const outreach_d = balance  * shareForDay;   // $      (assigned, share-based)

    // Capacity-adjusted reach used for the outcome columns.
    const reach   = contacts * oShare;           // count
    const reach_d = balance  * oShare;           // $

    const rpc_calc   = reach   * mRow.rpc_rate;                       // count
    const pay24_calc = reach_d * mRow.conversion_24hr_payment_rate;   // $

    const ptp_pct     = mRow.rpc_rate * mRow.ptp_of_rpc_rate;            // %
    const ptp_success = ptp_pct * mRow.conversion_kept_rate;            // %
    const collections = mRow.conversion_24hr_payment_rate + ptp_success; // %

    const total_collections   = collections * reach;    // count
    const total_collections_d = collections * reach_d;  // $

    return {
      outreach, outreach_d,
      rpc_calc, pay24_calc,
      ptp_pct, ptp_success, collections,
      total_collections, total_collections_d,
    };
  };

  // ── Save ─────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      await saveModelBaseline(modelId, {
        model_contacts: contactsRows.map((r) => ({
          day_of_week:      r.day_of_week,
          dpd_bucket_id:    r.dpd_bucket_id,
          contacts_count:   Math.max(0, Math.round(r.contacts_count)),
          contacts_balance: Math.max(0, r.contacts_balance),
        })),
        channel_metrics: metricsRows.map((r) => ({
          day_of_week:                   r.day_of_week,
          channel_id:                    r.channel_id,
          dpd_bucket_id:                 r.dpd_bucket_id,
          rpc_rate:                      clamp01(r.rpc_rate),
          conversion_24hr_payment_rate:  clamp01(r.conversion_24hr_payment_rate),
          ptp_of_rpc_rate:               clamp01(r.ptp_of_rpc_rate),
          conversion_kept_rate:          clamp01(r.conversion_kept_rate),
        })),
      });

      // Persist Capacity for every channel × day. Send the full matrix
      // (null where unset) so cleared values round-trip to NULL.
      const channelIds = [...new Set(metricsRows.map((r) => r.channel_id))];
      const attempts = [];
      for (const cid of channelIds) {
        for (let dow = 1; dow <= 7; dow++) {
          const cap = capacityMap[`${cid}|${dow}`];
          attempts.push({
            channel_id:  cid,
            day_of_week: dow,
            capacity:    cap == null ? null : Math.max(0, Math.round(cap)),
          });
        }
      }
      await saveChannelDayAttempts(modelId, { attempts });

      setToast({ open: true, message: "Baseline saved", severity: "success" });
      loadBaseline();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Failed to save");
      setToast({ open: true, message: "Save failed", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!window.confirm("Discard unsaved changes and reload from server?")) return;
    loadBaseline();
  };

  const selectedModel = models.find((m) => m.id === modelId);


  // ── Section collapse helpers ─────────────────────────────────
  const isCollapsed = (id) => collapsedSet.has(id);

  const toggleSection = (id) => {
    setCollapsedSet(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSectionIds = () => {
    const ids = ["contacts"];
    channelGroups.forEach(g => { ids.push(`channel-${g.channel_id}`); });
    return ids;
  };

  const expandAll   = () => setCollapsedSet(new Set());
  const collapseAll = () => setCollapsedSet(new Set(allSectionIds()));

  // Auto-collapse everything when a model's data first lands.
  useEffect(() => {
    if (modelId && channelGroups.length > 0 && initializedForModelRef.current !== modelId) {
      const ids = ["contacts"];
      channelGroups.forEach(g => { ids.push(`channel-${g.channel_id}`); });
      setCollapsedSet(new Set(ids));
      initializedForModelRef.current = modelId;
    }
  }, [modelId, channelGroups]);


  // ───────────────────────────────────────────────────────────────
  //   Day-of-week pill bar + Duplicate button
  // ───────────────────────────────────────────────────────────────
  const renderDayPills = () => (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1.5,
      mb: 2, mt: 1, pb: 1.5, borderBottom: "0.5px solid", borderColor: "divider",
    }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Editing day:
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5 }}>
        {DAY_NAMES.map((d) => {
          const selected = d.num === selectedDay;
          return (
            <Box
              key={d.num}
              onClick={saving ? undefined : () => setSelectedDay(d.num)}
              sx={{
                px: "12px", py: "5px",
                borderRadius: "20px",
                fontSize: 12, fontWeight: selected ? 600 : 500,
                cursor: saving ? "default" : "pointer", userSelect: "none",
                bgcolor: selected ? BLUE              : "transparent",
                color:   selected ? "#fff"            : "text.secondary",
                border:  selected ? "none"            : "0.5px solid",
                borderColor: "divider",
                "&:hover": selected ? {} : { bgcolor: "rgba(55,138,221,0.08)", color: BLUE },
              }}
              title={d.long}
            >
              {d.short}
            </Box>
          );
        })}
      </Box>

      <Button
        onClick={() => { setDuplicateTargets([]); setDuplicateOpen(true); }}
        disabled={saving}
        size="small"
        sx={{
          ml: 1, fontSize: 11, textTransform: "none",
          color: BLUE, borderRadius: "20px", px: 1.5,
          border: "0.5px solid", borderColor: "divider",
          "&:hover": { borderColor: BLUE, bgcolor: "rgba(55,138,221,0.05)" },
        }}
      >
        ⎘ Copy this day to…
      </Button>

      <Button
        onClick={collapsedSet.size === 0 ? collapseAll : expandAll}
        disabled={saving}
        size="small"
        sx={{
          fontSize: 11, textTransform: "none",
          color: "text.secondary", borderRadius: "20px", px: 1.5,
          border: "0.5px solid", borderColor: "divider",
          "&:hover": { borderColor: "text.primary", bgcolor: "rgba(0,0,0,0.03)" },
        }}
      >
        {collapsedSet.size === 0 ? "− Collapse all" : "+ Expand all"}
      </Button>
    </Box>
  );


  // ───────────────────────────────────────────────────────────────
  //   Contacts & Balance section (model-level, 8 rows × 2 inputs)
  // ───────────────────────────────────────────────────────────────
  const renderContactsSection = () => {
    if (contactsForDay.length === 0) return null;
    const total_contacts = contactsForDay.reduce((s, r) => s + r.contacts_count, 0);
    const total_balance  = contactsForDay.reduce((s, r) => s + r.contacts_balance, 0);

    const collapsed = isCollapsed("contacts");

    return (
      <Box sx={{ mb: 4 }}>
        <Box
          onClick={() => toggleSection("contacts")}
          sx={{
            display: "flex", alignItems: "baseline", gap: 1, mb: 1, mt: 1,
            pb: 1, borderBottom: `2px solid ${BLUE}`,
            cursor: "pointer", userSelect: "none",
            "&:hover": { opacity: 0.85 },
          }}
        >
          <Box sx={{
            fontSize: 9, color: "text.secondary", width: 12,
            display: "inline-block",
            transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
          }}>▼</Box>
          <SectionTitle>Unique Accounts & Delinquency$</SectionTitle>
          <Typography sx={{ fontSize: 11, color: "text.disabled", ml: 1 }}>
            Model-level: applies across all channels
          </Typography>
          {collapsed && (
            <Typography sx={{ fontSize: 11, color: "text.secondary", ml: "auto", mr: 0.5 }}>
              {total_contacts.toLocaleString()} unique accounts · {fmtCurrency(total_balance)}
            </Typography>
          )}
        </Box>

        {!collapsed && (
        <Box sx={{
          border: "0.5px solid", borderColor: "divider",
          borderRadius: 2, overflow: "hidden",
          maxWidth: 460,
        }}>
          {/* Header */}
          <Box sx={{
            display: "grid", gridTemplateColumns: "100px 140px 180px",
            px: 1.5, py: 1, gap: 1,
            background: "#f7f7f7",
            borderBottom: "0.5px solid", borderColor: "divider",
            fontSize: 11, fontWeight: 500,
            textTransform: "uppercase", letterSpacing: "0.05em",
            color: "text.secondary",
          }}>
            <Box>DPD Bucket</Box>
            <Box sx={{ textAlign: "right", color: BLUE }}>Unique Accounts</Box>
            <Box sx={{ textAlign: "right", color: BLUE }}>Delinquency$</Box>
          </Box>

          {/* Data rows */}
          {contactsForDay.map((row, idx) => (
            <Box key={row.dpd_bucket_id} sx={{
              display: "grid", gridTemplateColumns: "100px 140px 180px",
              px: 1.5, py: "6px", gap: 1,
              borderBottom: "0.5px solid", borderColor: "divider",
              background: idx % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
              alignItems: "center",
              "&:last-of-type": { borderBottom: "none" },
            }}>
              <Box sx={{ fontSize: 12, fontWeight: 500 }}>DPD {row.bucket_label}</Box>
              <NumInput
                value={row.contacts_count}
                onChange={(v) => setContactsCell(row.dpd_bucket_id, "contacts_count", v)}
                disabled={saving}
              />
              <NumInput
                value={row.contacts_balance}
                onChange={(v) => setContactsCell(row.dpd_bucket_id, "contacts_balance", v)}
                disabled={saving}
              />
            </Box>
          ))}

          {/* Totals */}
          <Box sx={{
            display: "grid", gridTemplateColumns: "100px 140px 180px",
            px: 1.5, py: "8px", gap: 1,
            background: "#f0f5ff",
            borderTop: `1px solid ${BLUE}33`,
            alignItems: "center",
          }}>
            <Box sx={{ fontSize: 11, fontWeight: 700, color: BLUE }}>Total</Box>
            <Box sx={{ textAlign: "right", pr: 1, fontSize: 13, fontWeight: 700, color: BLUE }}>
              {total_contacts.toLocaleString()}
            </Box>
            <Box sx={{ textAlign: "right", pr: 1, fontSize: 13, fontWeight: 700, color: BLUE }}>
              {fmtCurrency(total_balance)}
            </Box>
          </Box>
        </Box>
        )}
      </Box>
    );
  };


  // ───────────────────────────────────────────────────────────────
  //   Channel section — ONE table: 4 inputs interleaved with 9 calcs
  // ───────────────────────────────────────────────────────────────
  //
  // Column grid (14 cols). Inputs sit at RPC%, 24hr Pay%, PTP of RPC%,
  // PTP Kept%; everything else is read-only/derived.
  const GRID_COLS = "60px " + "repeat(13, minmax(70px, 1fr))";

  const renderChannelSection = (group, idx) => {
    const color = CHANNEL_COLORS[idx % CHANNEL_COLORS.length];
    const channelId = `channel-${group.channel_id}`;
    const channelCollapsed = isCollapsed(channelId);

    const shareToday          = getShareForDay(group.channel_id, group.channel_share_of_contacts);
    const totalContactsForDay = contactsForDay.reduce((s, r) => s + r.contacts_count,   0);
    const totalBalanceForDay  = contactsForDay.reduce((s, r) => s + r.contacts_balance, 0);
    const channelOutreach     = totalContactsForDay * shareToday;   // share-based (assigned)
    const channelOutreach_d   = totalBalanceForDay  * shareToday;
    const dayShort            = DAY_NAMES.find(d => d.num === selectedDay)?.short || "";

    // Capacity (manual). When set, the outcome columns use
    //   capPct = capacity ÷ total unique accounts (for the day)
    // in place of the channel share.
    const capacity     = getCapacity(group.channel_id);
    const capPct       = (capacity != null && capacity > 0 && totalContactsForDay > 0)
      ? capacity / totalContactsForDay
      : null;
    const outcomeShare = capPct != null ? capPct : shareToday;

    // Header RPC% = Σ RPC ÷ Σ Outreach (columns 5 ÷ 2). With no capacity this
    // is the blended rpc_rate; with capacity it reflects capPct ÷ share.
    const channelRpc = group.rows.reduce((s, r) => s + derive(r, shareToday, outcomeShare).rpc_calc, 0);
    const rpcPct     = channelOutreach > 0 ? (channelRpc / channelOutreach) * 100 : null;

    return (
      <Box key={group.channel_id} sx={{ mb: 4 }}>
        {/* Channel header */}
        <Box
          onClick={() => toggleSection(channelId)}
          sx={{
            display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 2, mb: 1, mt: 3,
            pb: 1, borderBottom: `2px solid ${color}`,
            cursor: "pointer", userSelect: "none",
            "&:hover": { opacity: 0.9 },
          }}
        >
          <Box sx={{
            fontSize: 9, color: "text.secondary", width: 12,
            display: "inline-block",
            transform: channelCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
          }}>▼</Box>
          <Box component="span" sx={{
            display: "inline-block", px: "10px", py: "3px",
            borderRadius: "20px", fontSize: 12, fontWeight: 600,
            bgcolor: color, color: "#fff",
          }}>
            {group.channel_name}
          </Box>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            Share for {dayShort}:{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
              {(shareToday * 100).toFixed(1)}%
            </Box>
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            Outreach:{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
              {fmtCount(channelOutreach)}
            </Box>
          </Typography>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            Outreach $:{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
              {fmtCurrency(channelOutreach_d)}
            </Box>
          </Typography>

          {/* Capacity (manual, per channel/day) + RPC contact % (derived from
              the Outreach and RPC columns). Stop click propagation so editing
              the field doesn't toggle the section collapse. Empty = null. */}
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
          >
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              Capacity:
            </Typography>
            <CapacityInput
              value={capacity}
              onChange={(v) => setCapacity(group.channel_id, v)}
              disabled={saving}
            />
            <Typography sx={{ fontSize: 11, color: "text.secondary", ml: 1 }}>
              {rpcPct != null ? (
                <>
                  RPC:{" "}
                  <Box component="span" sx={{ fontWeight: 600, color }}>
                    {rpcPct.toFixed(1)}%
                  </Box>
                </>
              ) : (
                <Box component="span" sx={{ color: "text.disabled" }}>RPC: —</Box>
              )}
            </Typography>
          </Box>
        </Box>

        {!channelCollapsed && (
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{
              minWidth: 1180,
              border: "0.5px solid", borderColor: "divider",
              borderRadius: 2, overflow: "hidden",
            }}>
              {/* Header row */}
              <Box sx={{
                display: "grid", gridTemplateColumns: GRID_COLS,
                px: 1, py: 1, gap: 0.75,
                background: "#f7f7f7",
                borderBottom: "0.5px solid", borderColor: "divider",
                fontSize: 10, fontWeight: 500,
                textTransform: "uppercase", letterSpacing: "0.03em",
              }}>
                <Box sx={{ color: "text.secondary" }}>DPD</Box>
                <Box sx={{ textAlign: "right", color: "text.secondary" }} title="Channel share × unique accounts for this DPD">Outreach</Box>
                <Box sx={{ textAlign: "right", color: "text.secondary" }} title="Channel share × delinquency$ for this DPD">Outreach $</Box>
                <Box sx={{ textAlign: "right", color }} title="Manual: % of outreach reached (RPC)">RPC %</Box>
                <Box sx={{ textAlign: "right", color: "text.secondary" }} title="Reach × RPC% (reach = capacity-based when Capacity is set)">RPC</Box>
                <Box sx={{ textAlign: "right", color }} title="Manual: % of delinquency$ paying within 24h">24hr Pay %</Box>
                <Box sx={{ textAlign: "right", color: "text.secondary" }} title="Reach$ × 24hr Pay%">24hr Pay $</Box>
                <Box sx={{ textAlign: "right", color }} title="Manual: PTP as % of RPC">PTP of RPC %</Box>
                <Box sx={{ textAlign: "right", color: "text.secondary" }} title="RPC% × PTP of RPC%">PTP %</Box>
                <Box sx={{ textAlign: "right", color }} title="Manual: % of PTPs kept">PTP Kept %</Box>
                <Box sx={{ textAlign: "right", color: "text.secondary" }} title="PTP% × PTP Kept%">PTP Success %</Box>
                <Box sx={{ textAlign: "right", color: "text.secondary" }} title="24hr Pay% + PTP Success%">Collections %</Box>
                <Box sx={{ textAlign: "right", color: "text.secondary" }} title="Collections% × reach">Total Coll.</Box>
                <Box sx={{ textAlign: "right", color: "text.secondary" }} title="Collections% × reach$">Total Coll. $</Box>
              </Box>

              {/* Data rows */}
              {group.rows.map((row, i) => {
                const d = derive(row, shareToday, outcomeShare);
                return (
                  <Box key={row.dpd_bucket_id} sx={{
                    display: "grid", gridTemplateColumns: GRID_COLS,
                    px: 1, py: "5px", gap: 0.75,
                    borderBottom: "0.5px solid", borderColor: "divider",
                    background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                    "&:last-of-type": { borderBottom: "none" },
                    alignItems: "center",
                  }}>
                    <Box sx={{ fontSize: 11, fontWeight: 500 }}>{row.bucket_label}</Box>

                    {/* Outreach / Outreach$ (derived, share-based) */}
                    <CalcCell value={fmtCount(d.outreach)} />
                    <CalcCell value={fmtCurrency(d.outreach_d)} />

                    {/* RPC% (input) → RPC (derived) */}
                    <NumInput value={row.rpc_rate} isRate disabled={saving}
                      onChange={(v) => setMetricsCell(row.channel_id, row.dpd_bucket_id, "rpc_rate", v)} />
                    <CalcCell value={fmtCount(d.rpc_calc)} color={AMBER} />

                    {/* 24hr Pay% (input) → 24hr Pay$ (derived) */}
                    <NumInput value={row.conversion_24hr_payment_rate} isRate disabled={saving}
                      onChange={(v) => setMetricsCell(row.channel_id, row.dpd_bucket_id, "conversion_24hr_payment_rate", v)} />
                    <CalcCell value={fmtCurrency(d.pay24_calc)} color={PURPLE} />

                    {/* PTP of RPC% (input) → PTP% (derived) */}
                    <NumInput value={row.ptp_of_rpc_rate} isRate disabled={saving}
                      onChange={(v) => setMetricsCell(row.channel_id, row.dpd_bucket_id, "ptp_of_rpc_rate", v)} />
                    <CalcCell value={fmtPct(d.ptp_pct)} color={BLUE} />

                    {/* PTP Kept% (input) → PTP Success% (derived) */}
                    <NumInput value={row.conversion_kept_rate} isRate disabled={saving}
                      onChange={(v) => setMetricsCell(row.channel_id, row.dpd_bucket_id, "conversion_kept_rate", v)} />
                    <CalcCell value={fmtPct(d.ptp_success)} color={GREEN} />

                    {/* Collections% → Total Collections / $ (derived) */}
                    <CalcCell value={fmtPct(d.collections)} color="text.primary" bold />
                    <CalcCell value={fmtCount(d.total_collections)} color={color} bold />
                    <CalcCell value={fmtCurrency(d.total_collections_d)} color={color} bold />
                  </Box>
                );
              })}

              {/* Totals row (sum count/$ columns; rates left blank) */}
              {(() => {
                const t = group.rows.reduce((acc, r) => {
                  const d = derive(r, shareToday, outcomeShare);
                  return {
                    outreach:            acc.outreach            + d.outreach,
                    outreach_d:          acc.outreach_d          + d.outreach_d,
                    rpc_calc:            acc.rpc_calc            + d.rpc_calc,
                    pay24_calc:          acc.pay24_calc          + d.pay24_calc,
                    total_collections:   acc.total_collections   + d.total_collections,
                    total_collections_d: acc.total_collections_d + d.total_collections_d,
                  };
                }, { outreach: 0, outreach_d: 0, rpc_calc: 0, pay24_calc: 0, total_collections: 0, total_collections_d: 0 });

                return (
                  <Box sx={{
                    display: "grid", gridTemplateColumns: GRID_COLS,
                    px: 1, py: "9px", gap: 0.75,
                    background: `${color}10`, borderTop: `1px solid ${color}33`,
                    alignItems: "center", fontSize: 12,
                  }}>
                    <Box sx={{ fontWeight: 700, color }}>Total</Box>
                    <Box sx={{ textAlign: "right", pr: "8px", fontWeight: 600 }}>{fmtCount(t.outreach)}</Box>
                    <Box sx={{ textAlign: "right", pr: "8px", fontWeight: 600 }}>{fmtCurrency(t.outreach_d)}</Box>
                    <Box />
                    <Box sx={{ textAlign: "right", pr: "8px", fontWeight: 600, color: AMBER }}>{fmtCount(t.rpc_calc)}</Box>
                    <Box />
                    <Box sx={{ textAlign: "right", pr: "8px", fontWeight: 600, color: PURPLE }}>{fmtCurrency(t.pay24_calc)}</Box>
                    <Box />
                    <Box />
                    <Box />
                    <Box />
                    <Box />
                    <Box sx={{ textAlign: "right", pr: "8px", fontWeight: 700, color }}>{fmtCount(t.total_collections)}</Box>
                    <Box sx={{ textAlign: "right", pr: "8px", fontWeight: 700, color }}>{fmtCurrency(t.total_collections_d)}</Box>
                  </Box>
                );
              })()}
            </Box>
          </Box>
        )}
      </Box>
    );
  };


  // ───────────────────────────────────────────────────────────────
  //   Render
  // ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{
        display: "flex", alignItems: "flex-end", gap: 2,
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Model
          </Typography>
          <Select
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
            size="small" variant="outlined"
            disabled={loadingModels || models.length === 0}
            displayEmpty
            sx={{
              fontSize: 12, borderRadius: "20px", minWidth: 220, height: 28,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "10px", fontSize: 12, color: "text.secondary" },
              ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
            }}
          >
            {models.length === 0 && (
              <MenuItem value="" disabled sx={{ fontSize: 12 }}>
                {loadingModels ? "Loading…" : "No models"}
              </MenuItem>
            )}
            {models.map((m) => (
              <MenuItem key={m.id} value={m.id} sx={{ fontSize: 12 }}>{m.name}</MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}>
          {saving && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 1, fontSize: 12, color: "text.secondary" }}>
              <CircularProgress size={14} /> Saving…
            </Box>
          )}
          <ActionButton onClick={handleReset} color="#888" disabled={saving || loadingRows}>↺ Reset</ActionButton>
          <Button
            onClick={handleSave}
            disabled={saving || loadingRows || contactsRows.length === 0}
            variant="contained"
            sx={{
              px: "14px", py: "5px", fontSize: 13, borderRadius: "20px",
              background: BLUE, color: "#fff", fontWeight: 500,
              textTransform: "none", boxShadow: "none",
              "&:hover": { background: "#2A6FB0", boxShadow: "none" },
              "&.Mui-disabled": { background: "#cccccc", color: "#fff" },
            }}
          >
            Save All
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {(loadingModels || loadingRows) && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loadingModels && models.length === 0 && (
        <EmptyState
          title="No models yet"
          hint="Create a model on the Models tab, then come back here to set up its baseline."
        />
      )}

      {!loadingRows && selectedModel && contactsRows.length === 0 && !loadingModels && (
        <EmptyState
          title="Baseline grid is empty"
          hint={`No rows came back for "${selectedModel.name}". Likely causes: dpd_buckets has no rows, day_of_week not seeded, or this model has no active channels (Models tab → Edit).`}
        />
      )}

      {!loadingRows && selectedModel && contactsRows.length > 0 && (
        <>
          {renderDayPills()}
          {renderContactsSection()}
          {channelGroups.map((g, i) => renderChannelSection(g, i))}
        </>
      )}

      {/* ── Duplicate-day dialog ── */}
      <Dialog
        open={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        maxWidth="xs" fullWidth
      >
        <DialogTitle sx={{ fontSize: 14, fontWeight: 500, pb: 1 }}>
          Copy {DAY_NAMES.find(d => d.num === selectedDay)?.long} to…
        </DialogTitle>
        <DialogContent dividers sx={{ py: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 1.5 }}>
            Overwrites the unique accounts, delinquency$, and per-channel rate inputs on the
            target day(s). Changes stay client-side until you click <b>Save All</b>.
          </Typography>

          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Checkbox
                size="small"
                checked={duplicateTargets.length === 6}
                indeterminate={duplicateTargets.length > 0 && duplicateTargets.length < 6}
                onChange={selectAllDuplicateTargets}
              />
            }
            label={<Typography sx={{ fontSize: 12, fontWeight: 500 }}>All other days</Typography>}
          />

          <Box sx={{ display: "flex", flexDirection: "column", pl: 0.5 }}>
            {DAY_NAMES.filter(d => d.num !== selectedDay).map(d => (
              <FormControlLabel
                key={d.num}
                control={
                  <Checkbox
                    size="small"
                    checked={duplicateTargets.includes(d.num)}
                    onChange={() => toggleDuplicateTarget(d.num)}
                  />
                }
                label={<Typography sx={{ fontSize: 12 }}>{d.long}</Typography>}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={() => setDuplicateOpen(false)}
            sx={{ textTransform: "none", fontSize: 12 }}>Cancel</Button>
          <Button
            onClick={handleDuplicateConfirm}
            disabled={duplicateTargets.length === 0}
            variant="contained"
            sx={{
              textTransform: "none", fontSize: 12, borderRadius: "20px",
              background: BLUE, "&:hover": { background: "#2A6FB0" },
              boxShadow: "none",
            }}
          >
            Copy
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          sx={{ fontSize: 13 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}